/**
 * BhiClinicalPdfPreviewModal.jsx — iter999 (Feb 2026)
 *
 * Fetches the redesigned clinical (physician-handoff) PDF from
 * `/api/bhi/tissue-restoration/physician-pdf`, previews it inline in the
 * browser (blob → iframe), and offers:
 *   • Download the PDF
 *   • Share with a physician — uses the native Web Share API with the file
 *     when available (mobile browsers); falls back to `mailto:` with a
 *     pre-filled subject/body reminding the sender to attach the just-
 *     downloaded file (desktop).
 *
 * No new backend endpoints — the existing physician-pdf route is reused.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Download, Share2, Mail, Loader2, FileText, ExternalLink } from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import useBhiSnapshot from "@/hooks/useBhiSnapshot";

export default function BhiClinicalPdfPreviewModal({ open, onClose }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const lang = language === "en" ? "en" : language === "es" ? "es" : "bg";
  const { data: snapshot } = useBhiSnapshot();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const blobRef = useRef(null);  // holds the raw Blob so we can share it as a File

  const T = {
    bg: {
      title: "Клиничен PDF · преглед",
      subtitle: "Редизайнираният доклад за твоя лекар — прегледай преди изтегляне.",
      download: "Изтегли PDF",
      share: "Сподели с лекар",
      email: "Изпрати по e-mail",
      close: "Затвори",
      loading: "Генерираме PDF за преглед…",
      openTab: "Отвори в нов раздел",
      shareSubject: "Клиничен BHI доклад (Nutramilo)",
      shareBody:
        "Здравейте,\n\nПрилагам моят актуален Breast Health Intelligence клиничен доклад " +
        "от Nutramilo. Моля, прикачете файла nutramilo_bhi_handoff.pdf, който току-що " +
        "изтеглих, преди да изпратите този e-mail.\n\nБлагодаря!",
      shareTitle: "BHI клиничен доклад",
      errFallback: "PDF генерирането се провали. Опитай отново.",
    },
    en: {
      title: "Clinical PDF · preview",
      subtitle: "The redesigned physician handoff — review before downloading.",
      download: "Download PDF",
      share: "Share with physician",
      email: "Send by email",
      close: "Close",
      loading: "Generating PDF preview…",
      openTab: "Open in new tab",
      shareSubject: "BHI Clinical Report (Nutramilo)",
      shareBody:
        "Hello,\n\nAttached is my up-to-date Breast Health Intelligence clinical report " +
        "from Nutramilo. Please attach the file nutramilo_bhi_handoff.pdf I just " +
        "downloaded before sending this email.\n\nThank you!",
      shareTitle: "BHI clinical report",
      errFallback: "PDF generation failed. Please try again.",
    },
    es: {
      title: "PDF clínico · vista previa",
      subtitle: "El informe rediseñado para tu médico.",
      download: "Descargar PDF",
      share: "Compartir con médico",
      email: "Enviar por email",
      close: "Cerrar",
      loading: "Generando PDF…",
      openTab: "Abrir en nueva pestaña",
      shareSubject: "Informe clínico BHI (Nutramilo)",
      shareBody: "Hola,\n\nAdjunto mi informe clínico BHI actual.\n\nGracias!",
      shareTitle: "Informe clínico BHI",
      errFallback: "Error generando el PDF.",
    },
  }[lang] || {};

  const fetchPdf = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const resp = await axios.post(
        `${API}/bhi/tissue-restoration/physician-pdf`,
        { bhi_snapshot: snapshot || {}, lang },
        { responseType: "blob", timeout: 60000 },
      );
      const b = new Blob([resp.data], { type: "application/pdf" });
      blobRef.current = b;
      const url = window.URL.createObjectURL(b);
      setBlobUrl(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || T.errFallback);
    } finally {
      setBusy(false);
    }
  }, [API, snapshot, lang, T.errFallback]);

  useEffect(() => {
    if (!open) return;
    fetchPdf();
    return () => {
      if (blobRef.current) blobRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const filename = `nutramilo_bhi_handoff_${new Date().toISOString().slice(0, 10)}.pdf`;

  const doDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const doShare = async () => {
    if (!blobRef.current) return;
    // Prefer Web Share Level 2 with files (mobile Safari/Chrome).
    try {
      const file = new File([blobRef.current], filename, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: T.shareTitle, text: T.shareSubject });
        return;
      }
    } catch (e) {
      // fall through to mailto:
    }
    // Fallback — mailto: (browsers can't attach files programmatically).
    // Trigger download first so the user has the file ready to attach.
    doDownload();
    const mail =
      `mailto:?subject=${encodeURIComponent(T.shareSubject)}` +
      `&body=${encodeURIComponent(T.shareBody)}`;
    window.location.href = mail;
  };

  const doOpenTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      role="dialog"
      aria-modal="true"
      data-testid="bhi-pdf-preview-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className={`w-full max-w-5xl h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border ${
          isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-900"
        }`}
      >
        {/* Header */}
        <div className={`flex items-center gap-3 px-5 py-3 border-b ${
          isDark ? "border-slate-800" : "border-slate-200"
        }`}>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
            isDark ? "bg-pink-500/20 text-pink-300" : "bg-pink-100 text-pink-700"
          }`}>
            <FileText className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{T.title}</div>
            <div className={`text-[11px] truncate ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {T.subtitle}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bhi-pdf-preview-close"
            className={`p-2 rounded-lg transition ${
              isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"
            }`}
            aria-label={T.close}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className={`flex flex-wrap items-center gap-2 px-5 py-2 border-b ${
          isDark ? "bg-slate-900 border-slate-800" : "bg-slate-50 border-slate-200"
        }`}>
          <button
            type="button"
            onClick={doDownload}
            disabled={!blobUrl || busy}
            data-testid="bhi-pdf-preview-download"
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {T.download}
          </button>
          <button
            type="button"
            onClick={doShare}
            disabled={!blobUrl || busy}
            data-testid="bhi-pdf-preview-share"
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${
              isDark
                ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20"
                : "border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
            }`}
            title={T.share}
          >
            <Share2 className="w-4 h-4" />
            {T.share}
          </button>
          <button
            type="button"
            onClick={doOpenTab}
            disabled={!blobUrl || busy}
            data-testid="bhi-pdf-preview-newtab"
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${
              isDark
                ? "border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <ExternalLink className="w-4 h-4" />
            {T.openTab}
          </button>
        </div>

        {/* Body */}
        <div className={`flex-1 min-h-0 relative ${isDark ? "bg-slate-900" : "bg-slate-100"}`}>
          {busy && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-pink-300" : "text-pink-600"}`} />
              <div className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>{T.loading}</div>
            </div>
          )}
          {err && !busy && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6">
              <div className="text-sm text-red-500 text-center max-w-md">{err}</div>
              <button
                type="button"
                onClick={fetchPdf}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-pink-500 text-white hover:bg-pink-600"
              >
                Опитай отново
              </button>
            </div>
          )}
          {blobUrl && !busy && (
            <iframe
              title="BHI Clinical PDF preview"
              src={blobUrl}
              className="w-full h-full border-0"
              data-testid="bhi-pdf-preview-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}
